import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { elementToPdf } from "@/lib/pdf";
import ReceiptDocument, { buildReceiptText } from "@/components/ReceiptDocument";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { MessageCircle, Printer, Download, Copy, FileText, Receipt as RcptIcon, Share2, Link as LinkIcon, Smartphone } from "lucide-react";
import { openSms, buildReceiptMessage } from "@/lib/notify";

export default function Receipt() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const autoPrint = searchParams.get("print") === "1";
  const [order, setOrder] = useState(null);
  const [s, setS] = useState({});
  const [loyalty, setLoyalty] = useState(null);
  const [view, setView] = useState(autoPrint ? "ticket" : "a4");
  const [busy, setBusy] = useState(false);
  const docRef = useRef(null);
  const printedRef = useRef(false);

  const load = useCallback(() => {
    api.get(`/orders/${id}/receipt`).then((r) => { setOrder(r.data.order); setS(r.data.settings || {}); setLoyalty(r.data.loyalty || null); }).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (autoPrint && order && !printedRef.current) {
      printedRef.current = true;
      const t = setTimeout(() => window.print(), 600);
      return () => clearTimeout(t);
    }
  }, [autoPrint, order]);

  if (!order) return <p className="text-slate-400">Chargement...</p>;
  const cur = s.currency || "KMF";
  const o = order;
  const publicLink = `${window.location.origin}/r/${o.id}`;

  const whatsapp = () => {
    const phone = (o.customer_phone || "").replace(/\D/g, "");
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(buildReceiptText(o, s, cur, publicLink))}`, "_blank");
  };
  const copy = async () => { try { await navigator.clipboard.writeText(buildReceiptText(o, s, cur, publicLink)); toast.success("Reçu copié"); } catch { toast.error("Copie impossible"); } };
  const sms = () => openSms(o.customer_phone, buildReceiptMessage(o, s, publicLink));
  const copyLink = async () => { try { await navigator.clipboard.writeText(publicLink); toast.success("Lien copié"); } catch { toast.error("Copie impossible"); } };
  const print = () => window.print();

  const downloadPdf = async () => {
    if (!docRef.current) return;
    setBusy(true);
    try {
      await elementToPdf(docRef.current, `recu-${o.order_number}.pdf`);
      toast.success("PDF téléchargé");
    } catch { toast.error("Échec de la génération du PDF"); }
    finally { setBusy(false); }
  };

  const share = async () => {
    setBusy(true);
    try {
      let file = null;
      if (docRef.current) {
        try {
          const { default: html2canvas } = await import("html2canvas");
          const canvas = await html2canvas(docRef.current, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
          const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
          if (blob) file = new File([blob], `recu-${o.order_number}.png`, { type: "image/png" });
        } catch (e) { console.debug("Capture image du reçu échouée, repli texte:", e); }
      }
      const shareData = { title: `Reçu ${o.order_number}`, text: buildReceiptText(o, s, cur, publicLink) };
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ ...shareData, files: [file] });
      } else if (navigator.share) {
        await navigator.share({ ...shareData, url: publicLink });
      } else {
        await navigator.clipboard.writeText(publicLink);
        toast.success("Lien copié (partage non supporté)");
      }
    } catch { /* user cancelled */ }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <div className="no-print flex flex-wrap items-center gap-2 sticky top-0 z-10 bg-slate-50/90 backdrop-blur py-2">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
          <button data-testid="receipt-view-a4" onClick={() => setView("a4")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium ${view === "a4" ? "bg-sky-500 text-white" : "text-slate-600"}`}><FileText size={15} /> Facture A4</button>
          <button data-testid="receipt-view-ticket" onClick={() => setView("ticket")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium ${view === "ticket" ? "bg-sky-500 text-white" : "text-slate-600"}`}><RcptIcon size={15} /> Ticket</button>
        </div>
        <Button data-testid="receipt-share" onClick={share} disabled={busy} className="bg-sky-600 hover:bg-sky-700"><Share2 size={16} className="mr-1.5" /> Partager</Button>
        <Button variant="outline" data-testid="receipt-whatsapp" onClick={whatsapp} className="text-emerald-600 border-emerald-200"><MessageCircle size={16} className="mr-1.5" /> WhatsApp</Button>
        <Button variant="outline" data-testid="receipt-sms" onClick={sms}><Smartphone size={16} className="mr-1.5" /> SMS</Button>
        <Button variant="outline" data-testid="receipt-pdf" onClick={downloadPdf} disabled={busy}><Download size={16} className="mr-1.5" /> PDF</Button>
        <Button variant="outline" data-testid="receipt-print" onClick={print}><Printer size={16} className="mr-1.5" /> Imprimer</Button>
        <Button variant="outline" data-testid="receipt-copylink" onClick={copyLink}><LinkIcon size={16} className="mr-1.5" /> Lien</Button>
        <Button variant="outline" data-testid="receipt-copy" onClick={copy}><Copy size={16} className="mr-1.5" /> Copier</Button>
      </div>

      <div className="print-area" ref={docRef}>
        <ReceiptDocument o={o} s={s} cur={cur} view={view} loyalty={loyalty} />
      </div>
    </div>
  );
}
